import React, { useEffect, useState } from "react";
import {
  fetchPartnerHotels, createPartnerHotel, updatePartnerHotel, deletePartnerHotel,
  fetchPartnerPayables, recordPartnerPayment, getCurrency,
  refreshMe, updateRestaurantSettings,
} from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

// Front Desk — partner hotels for outsourced / overflow rooms. One page with
// two areas: a registry of partner hotels, and the amounts owed to them
// (payables) with an inline "record payment" control.
// AM/OM are machine drafts (2026-08) — pending native review.
const S = {
  en: {
    title: "Partner hotels", subtitle: "Overflow & outsourced rooms — partners and what we owe them",
    reg_heading: "Partner hotels", name: "Hotel name", contact: "Contact name", phone: "Phone",
    rate: "Default nightly rate", notes: "Notes", add: "Add partner", save: "Save", cancel: "Cancel",
    edit: "Edit", deact: "Deactivate", deact_q: "Deactivate this partner hotel?",
    none: "No partner hotels yet. Add one to route overflow rooms.",
    name_req: "Hotel name is required.", could_not_save: "Could not save.", active: "Active",
    pay_heading: "Amounts owed to partners", partner: "Partner", owed: "Owed", paid: "Paid",
    balance: "Balance", totals: "Totals", record_pay: "Record payment", amount: "Amount",
    note: "Note (optional)", pay: "Pay", pay_err: "Could not record payment.",
    no_payables: "Nothing owed to partners yet.",
    track_pay: "Track what we owe partners", track_off_hint: "Payables tracking is off. Turn it on to record and see amounts owed to partner hotels.",
  },
  am: {
    title: "አጋር ሆቴሎች", subtitle: "የተትረፈረፉ እና የውጭ ክፍሎች — አጋሮች እና የምንከፍላቸው",
    reg_heading: "አጋር ሆቴሎች", name: "የሆቴል ስም", contact: "የአድራሻ ስም", phone: "ስልክ",
    rate: "ነባሪ የሌሊት ዋጋ", notes: "ማስታወሻ", add: "አጋር ጨምር", save: "አስቀምጥ", cancel: "ሰርዝ",
    edit: "አርትዕ", deact: "አቦዝን", deact_q: "ይህን አጋር ሆቴል ማቦዘን?",
    none: "እስካሁን አጋር ሆቴሎች የሉም። ትርፍ ክፍሎችን ለመላክ አንድ ይጨምሩ።",
    name_req: "የሆቴል ስም ያስፈልጋል።", could_not_save: "ማስቀመጥ አልተቻለም።", active: "ንቁ",
    pay_heading: "ለአጋሮች የሚከፈል ገንዘብ", partner: "አጋር", owed: "የተከፈለ ወጪ", paid: "የተከፈለ",
    balance: "ቀሪ", totals: "ጠቅላላ", record_pay: "ክፍያ መዝግብ", amount: "መጠን",
    note: "ማስታወሻ (አማራጭ)", pay: "ክፈል", pay_err: "ክፍያ መመዝገብ አልተቻለም።",
    no_payables: "እስካሁን ለአጋሮች የሚከፈል የለም።",
    track_pay: "ለአጋሮች የምንከፍለውን ተከታተል", track_off_hint: "የክፍያ ክትትል ጠፍቷል። ለአጋር ሆቴሎች የሚከፈለውን ለመመዝገብና ለማየት ያብሩት።",
  },
  om: {
    title: "Hoteelota michuu", subtitle: "Kutaalee dabalataa fi ala — michuuwwan fi waan isaaniif kaffallu",
    reg_heading: "Hoteelota michuu", name: "Maqaa hoteelaa", contact: "Maqaa qunnamtii", phone: "Bilbila",
    rate: "Gatii halkan tokkoo", notes: "Yaadannoo", add: "Michuu dabali", save: "Kaa'i", cancel: "Haqi",
    edit: "Gulaali", deact: "Dhaamsi", deact_q: "Hoteela michuu kana dhaamsuu?",
    none: "Ammatti hoteelli michuu hin jiru. Kutaalee dabalataa erguuf tokko dabali.",
    name_req: "Maqaan hoteelaa barbaachisaa dha.", could_not_save: "Kaa'uu hin dandeenye.", active: "Kan hojjetu",
    pay_heading: "Maallaqa michuuwwaniif kaffalamu", partner: "Michuu", owed: "Kaffalamuu qabu", paid: "Kaffalame",
    balance: "Hafe", totals: "Ida'ama", record_pay: "Kaffaltii galmeessi", amount: "Hanga",
    note: "Yaadannoo (filannoo)", pay: "Kaffali", pay_err: "Kaffaltii galmeessuu hin dandeenye.",
    no_payables: "Ammatti michuuwwaniif kaffalamu hin jiru.",
    track_pay: "Waan michuuwwaniif kaffallu hordofi", track_off_hint: "Hordoffiin kaffaltii cufameera. Waan hoteelota michuuf kaffalamu galmeessuu fi ilaaluuf banaa.",
  },
};

const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const empty = { name: "", contact_name: "", contact_phone: "", default_rate: "", notes: "" };
const TH = { textAlign: "left", color: "#5b6472", padding: "3px 6px" };
const TR = { textAlign: "right" };

export default function PartnerHotelsPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";

  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [err, setErr] = useState("");

  const [pay, setPay] = useState(null);
  const [payErr, setPayErr] = useState("");
  const [payOpenId, setPayOpenId] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payNote, setPayNote] = useState("");
  const [trackPay, setTrackPay] = useState(true);   // per-hotel setting; loaded from /auth/me

  const loadRows = () => fetchPartnerHotels().then(setRows).catch(() => setRows([]));
  const loadPay = () => fetchPartnerPayables().then(setPay).catch(() => setPay({ rows: [], totals: { owed: 0, paid: 0, balance: 0 } }));
  useEffect(() => {
    loadRows(); loadPay();
    refreshMe().then((me) => setTrackPay(me.track_partner_payables !== false)).catch(() => {});
  }, []);

  const toggleTrackPay = async () => {
    const next = !trackPay;
    setTrackPay(next);   // optimistic
    try {
      await updateRestaurantSettings({ track_partner_payables: next });
      if (next) loadPay();
    } catch { setTrackPay(!next); }   // revert on failure
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim()) { setErr(t("name_req")); return; }
    const body = {
      name: form.name.trim(),
      contact_name: form.contact_name.trim() || null,
      contact_phone: form.contact_phone.trim() || null,
      default_rate: form.default_rate === "" ? null : Number(form.default_rate),
      notes: form.notes.trim() || null,
    };
    try {
      if (editId) await updatePartnerHotel(editId, body); else await createPartnerHotel(body);
      setForm(empty); setEditId(null); loadRows();
    } catch { setErr(t("could_not_save")); }
  };

  const startEdit = (p) => {
    setEditId(p.id);
    setForm({
      name: p.name || "", contact_name: p.contact_name || "", contact_phone: p.contact_phone || "",
      default_rate: p.default_rate ?? "", notes: p.notes || "",
    });
  };

  const remove = async (p) => {
    if (!window.confirm(t("deact_q"))) return;
    try { await deletePartnerHotel(p.id); } catch { /* ignore */ }
    loadRows(); loadPay();
  };

  const togglePay = (partnerId) => {
    if (payOpenId === partnerId) { setPayOpenId(null); return; }
    setPayOpenId(partnerId); setPayAmount(""); setPayNote(""); setPayErr("");
  };

  const submitPay = async (partnerId) => {
    setPayErr("");
    const amt = Number(payAmount);
    if (!(amt > 0)) { setPayErr(t("pay_err")); return; }
    try {
      await recordPartnerPayment(partnerId, { amount: amt, ...(payNote.trim() ? { note: payNote.trim() } : {}) });
      setPayOpenId(null); setPayAmount(""); setPayNote("");
      loadPay();
    } catch { setPayErr(t("pay_err")); }
  };

  const inp = { padding: 8, borderRadius: 8, border: "1px solid #d8dee2" };
  const totals = (pay && pay.totals) || { owed: 0, paid: 0, balance: 0 };

  return (
    <div style={{ maxWidth: 940, margin: "0 auto" }}>
      <h1 style={{ margin: "6px 0 2px" }}>{t("title")}</h1>
      <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>

      {/* ---- Registry ---- */}
      <h2 style={{ margin: "18px 0 6px", fontSize: 18 }}>{t("reg_heading")}</h2>
      <form onSubmit={submit} style={{ ...CARD, padding: 14, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("name")} style={{ ...inp, width: 180 }} />
        <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder={t("contact")} style={{ ...inp, width: 150 }} />
        <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder={t("phone")} style={{ ...inp, width: 130 }} />
        <input value={form.default_rate} onChange={(e) => setForm({ ...form, default_rate: e.target.value })} placeholder={t("rate")} type="number" min="0" style={{ ...inp, width: 150 }} />
        <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder={t("notes")} style={{ ...inp, width: 200 }} />
        <button className="btn primary small" type="submit">{editId ? t("save") : t("add")}</button>
        {editId && <button className="btn secondary small" type="button" onClick={() => { setEditId(null); setForm(empty); }}>{t("cancel")}</button>}
        {err && <span style={{ color: "#A32D2D", fontSize: 13, width: "100%" }}>{err}</span>}
      </form>

      {rows == null ? (
        <p className="muted" style={{ marginTop: 14 }}>…</p>
      ) : rows.length === 0 ? (
        <div style={{ ...CARD, padding: 20, marginTop: 14, textAlign: "center" }} className="muted">{t("none")}</div>
      ) : (
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
          {rows.map((p) => (
            <div key={p.id} style={{ ...CARD, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{p.name}</div>
                <div className="muted" style={{ fontSize: 13 }}>
                  {p.contact_name || ""}{p.contact_phone ? ` · ${p.contact_phone}` : ""}
                  {p.default_rate != null ? ` · ${t("rate")} ${formatPrice(p.default_rate, cur)}` : ""}
                  {p.notes ? ` · ${p.notes}` : ""}
                </div>
              </div>
              <button className="btn secondary small" onClick={() => startEdit(p)}>{t("edit")}</button>
              <button onClick={() => remove(p)} style={{ fontSize: 13, background: "none", border: "none", color: "#A32D2D", cursor: "pointer" }}>{t("deact")}</button>
            </div>
          ))}
        </div>
      )}

      {/* ---- Payables ---- */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, margin: "24px 0 6px" }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>{t("pay_heading")}</h2>
        <label style={{ fontSize: 13, color: "#5b6472", display: "flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={trackPay} onChange={toggleTrackPay} />
          {t("track_pay")}
        </label>
      </div>
      {!trackPay ? (
        <div style={{ ...CARD, padding: 20, textAlign: "center" }} className="muted">{t("track_off_hint")}</div>
      ) : pay == null ? (
        <p className="muted" style={{ marginTop: 8 }}>…</p>
      ) : (pay.rows || []).length === 0 ? (
        <div style={{ ...CARD, padding: 20, textAlign: "center" }} className="muted">{t("no_payables")}</div>
      ) : (
        <div style={{ ...CARD, padding: 14 }}>
          <table style={{ width: "100%", fontSize: 13.5, borderCollapse: "collapse" }}>
            <thead><tr>
              <th style={TH}>{t("partner")}</th>
              <th style={{ ...TH, ...TR }}>{t("owed")}</th>
              <th style={{ ...TH, ...TR }}>{t("paid")}</th>
              <th style={{ ...TH, ...TR }}>{t("balance")}</th>
              <th style={TH}></th>
            </tr></thead>
            <tbody>
              {pay.rows.map((r) => (
                <React.Fragment key={r.partner_id}>
                  <tr>
                    <td style={{ padding: "4px 6px", fontWeight: 600 }}>{r.name}</td>
                    <td style={TR}>{formatPrice(r.owed || 0, cur)}</td>
                    <td style={TR}>{formatPrice(r.paid || 0, cur)}</td>
                    <td style={{ ...TR, color: (r.balance || 0) > 0 ? "#8A5A0B" : "#1D7A44", fontWeight: 600 }}>{formatPrice(r.balance || 0, cur)}</td>
                    <td style={{ ...TR }}>
                      <button className="btn secondary small" onClick={() => togglePay(r.partner_id)}>{t("record_pay")}</button>
                    </td>
                  </tr>
                  {payOpenId === r.partner_id && (
                    <tr>
                      <td colSpan={5} style={{ padding: "6px 6px 10px" }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                          <input value={payAmount} onChange={(e) => setPayAmount(e.target.value)} placeholder={t("amount")} type="number" min="0" style={{ ...inp, width: 130 }} />
                          <input value={payNote} onChange={(e) => setPayNote(e.target.value)} placeholder={t("note")} style={{ ...inp, width: 220 }} />
                          <button className="btn primary small" onClick={() => submitPay(r.partner_id)}>{t("pay")}</button>
                          {payErr && <span style={{ color: "#A32D2D", fontSize: 13 }}>{payErr}</span>}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: "1px solid #ddd", fontWeight: 700 }}>
                <td style={{ padding: "5px 6px" }}>{t("totals")}</td>
                <td style={TR}>{formatPrice(totals.owed || 0, cur)}</td>
                <td style={TR}>{formatPrice(totals.paid || 0, cur)}</td>
                <td style={{ ...TR, color: (totals.balance || 0) > 0 ? "#8A5A0B" : "#1D7A44" }}>{formatPrice(totals.balance || 0, cur)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
